import type { Vec2 } from '../../geo/types';
import { transformDirection, transformPoint } from '../mesh/meshBuilder';
import { panelPoint } from './panels';
import { wallFace, type Body, type FacadeContext, type WallFace } from './houseFacades';
import type { HouseOpenings, Opening } from './openings';
import type { HouseStreets } from './houseStreets';

/*
 * Iron anchors (schootankers) tie a brick wall to the beam layers behind it: a vertical bar about
 * 80 cm long through the eye of a tie, straight and black before the decorated anchors of the later
 * sixteenth century (Rouwhorst, Binnenstad 240; docs/plans/houses.md, section 8.6).
 */
export const ANCHOR = {
  length: 0.8,
  /** The template's width: the tie's eye across the bar. */
  width: 0.1,
  /** From a wall's end to the nearest anchor: the beam's bearing in the wall beside it. */
  inset: 0.35,
  /** Along a side wall, about a bay between the bents the anchors tie to. */
  spacing: 2.5,
  /** Kept between an anchor and an opening's edge, and under the wall's top. */
  clear: 0.1,
} as const;

/** A beam layer at every floor and the tie beams at the eaves. */
export function anchorLevels(storeys: number, storeyHeight: number): number[] {
  return Array.from({ length: storeys }, (_, k) => (k + 1) * storeyHeight);
}

export function frontColumns(width: number): number[] {
  return [ANCHOR.inset, width - ANCHOR.inset];
}

/** Evenly along a side wall from `from` to `to`, at most a bay apart, with one near each end. */
export function sideColumns(from: number, to: number): number[] {
  const [first, last] = [from + ANCHOR.inset, to - ANCHOR.inset];
  const gaps = Math.max(1, Math.ceil((last - first) / ANCHOR.spacing));
  return Array.from({ length: gaps + 1 }, (_, k) => first + ((last - first) * k) / gaps);
}

function clearOf(u: number, y: number, o: Opening): boolean {
  const [halfW, halfL] = [ANCHOR.width / 2, ANCHOR.length / 2];
  return (
    u + halfW <= o.x - ANCHOR.clear ||
    u - halfW >= o.x + o.width + ANCHOR.clear ||
    y + halfL <= o.y - ANCHOR.clear ||
    y - halfL >= o.y + o.height + ANCHOR.clear
  );
}

/**
 * Anchor centres in a wall's panel coordinates: at every level and column, lowered to stay under the
 * wall's `top`, and left out where the anchor would cross an opening.
 */
export function anchorSpots(
  columns: readonly number[],
  levels: readonly number[],
  openings: readonly Opening[],
  top: number
): Vec2[] {
  const highest = top - ANCHOR.length / 2 - ANCHOR.clear;
  return levels.flatMap((level) => {
    const y = Math.min(level, highest);
    return columns.filter((u) => openings.every((o) => clearOf(u, y, o))).map((u): Vec2 => [u, y]);
  });
}

function placeAnchors(ctx: FacadeContext, face: WallFace, spots: readonly Vec2[]): void {
  const right = transformDirection(face.panel.uAxis, ctx.frame);
  const outward = transformDirection(face.outward, ctx.frame);
  for (const [u, y] of spots) {
    const corner = panelPoint(face.panel, u - ANCHOR.width / 2, y - ANCHOR.length / 2);
    const origin = transformPoint(corner, ctx.frame);
    ctx.details.add(
      'anchor',
      { origin, right, outward, width: ANCHOR.width, height: ANCHOR.length },
      ctx.edgeId
    );
  }
}

/** A side wall as its face, the stretch of it every storey shares, and its openings. */
interface SideWall {
  readonly face: WallFace;
  readonly from: number;
  readonly to: number;
  readonly openings: readonly Opening[];
}

/* Only a side wall along a street shows its anchors; a neighbour's wall hides the others. */
function anchorSides(
  ctx: FacadeContext,
  walls: { left: SideWall; right: SideWall },
  streets: HouseStreets,
  levels: readonly number[],
  eaves: number
): void {
  for (const side of ['left', 'right'] as const) {
    if (!streets[side]) continue;
    const { face, from, to, openings } = walls[side];
    placeAnchors(ctx, face, anchorSpots(sideColumns(from, to), levels, openings, eaves));
  }
}

/** A brick house's anchors: across its front, slab or plain, and along its side walls on a street. */
export function anchorBrickHouse(
  ctx: FacadeContext,
  body: Body,
  levels: readonly number[],
  openings: HouseOpenings,
  streets: HouseStreets
): void {
  const { half, depth, x0, eaves } = body;
  const front = [...openings.front, ...openings.frontGable];
  /* Under the eaves at a front's corners too: over them a gable's edge or its first step may fall within an anchor's length. */
  placeAnchors(
    ctx,
    wallFace([0, 0, -half], [-1, 0, 0]),
    anchorSpots(frontColumns(2 * half), levels, front, eaves)
  );
  const length = depth - x0;
  anchorSides(
    ctx,
    {
      left: {
        face: wallFace([depth, 0, -half], [0, 0, -1]),
        from: 0,
        to: length,
        openings: openings.left,
      },
      right: {
        face: wallFace([x0, 0, half], [0, 0, 1]),
        from: 0,
        to: length,
        openings: openings.right,
      },
    },
    streets,
    levels,
    eaves
  );
}

/** A timber house's anchors: its front is timber, so only its brick side walls on a street, behind the deepest storey. */
export function anchorTimberHouse(
  ctx: FacadeContext,
  body: Body,
  levels: readonly number[],
  deepest: number,
  openings: HouseOpenings,
  streets: HouseStreets
): void {
  const { half, depth, eaves } = body;
  anchorSides(
    ctx,
    {
      left: {
        face: wallFace([depth, 0, -half], [0, 0, -1]),
        from: 0,
        to: depth - deepest,
        openings: openings.left,
      },
      right: {
        face: wallFace([0, 0, half], [0, 0, 1]),
        from: deepest,
        to: depth,
        openings: openings.right,
      },
    },
    streets,
    levels,
    eaves
  );
}
